import { ContentSkeleton } from '@/components/loading/Skeleton'
import { useState, useEffect, useCallback, type FormEvent } from 'react'
import { api } from '@/util/api'
import { Button, Card, ConfirmDialog, CopyField, Dialog, DialogClose, EmptyState, IconButton, Notice, TextField, useToast } from '@/components/play'
import { KeyRound, Plus, RefreshCw, Trash2 } from 'lucide-react'
import type { TokenResource } from '@/types/tokens'
import '@/components/settings/event-settings.css'
import '@/components/account/account.css'

const CREATE_FORM_ID = 'api-token-create'

export function ApiTokensSection() {
  const [tokens, setTokens] = useState<TokenResource[]>([])
  const [loading, setLoading] = useState(true)
  // A failed load says so; it never shows the empty state, which would claim there are no tokens.
  const [loadFailed, setLoadFailed] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [newTokenName, setNewTokenName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createdToken, setCreatedToken] = useState<string | null>(null)
  const { toast } = useToast()

  const fetchTokens = useCallback(async () => {
    try {
      const res = await api.getTokens()
      setTokens(res.data)
      setLoadFailed(false)
    } catch {
      setLoadFailed(true)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTokens()
  }, [fetchTokens])

  const retryLoad = () => {
    setLoading(true)
    fetchTokens()
  }

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault()
    if (!newTokenName.trim() || creating) return
    setCreating(true)
    try {
      const res = await api.createToken({ name: newTokenName.trim() })
      setCreatedToken(res.data.plaintext!)
      setNewTokenName('')
      fetchTokens()
    } catch (err: any) {
      const message = err?.response?.data?.error?.message || 'Check your connection and try again.'
      toast({ title: 'Token not created', description: message, tone: 'error' })
    } finally {
      setCreating(false)
    }
  }

  // Runs from the revoke confirmation, which shows progress until this settles and keeps a failure in the dialog.
  const handleRevoke = async (tokenId: string) => {
    await api.revokeToken(tokenId)
    toast({ title: 'Token revoked', tone: 'success' })
    fetchTokens()
  }

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setCreatedToken(null)
      setNewTokenName('')
    }
    setCreateOpen(open)
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const createDialog = (
    <Dialog
      open={createOpen}
      onOpenChange={handleDialogClose}
      trigger={
        <Button size="compact">
          <Plus aria-hidden="true" />
          Create token
        </Button>
      }
      title={createdToken ? 'Token created' : 'Create API token'}
      description={createdToken ? undefined : 'Name it for where you’ll use it.'}
      footer={
        createdToken ? (
          <DialogClose variant="primary">Done</DialogClose>
        ) : (
          <>
            <DialogClose>Cancel</DialogClose>
            <Button type="submit" form={CREATE_FORM_ID} disabled={!newTokenName.trim()} loading={creating}>
              Create token
            </Button>
          </>
        )
      }
    >
      {createdToken ? (
        <div className="space-y-3">
          <CopyField label="API token" value={createdToken} />
          <Notice tone="warning">Copy it now. You won’t see it again.</Notice>
        </div>
      ) : (
        <form id={CREATE_FORM_ID} onSubmit={handleCreate}>
          <TextField
            label="Token name"
            placeholder="e.g. AI Producer skill, MCP server"
            value={newTokenName}
            onChange={(e) => setNewTokenName(e.target.value)}
          />
        </form>
      )}
    </Dialog>
  )

  return (
    <section className="ps-section" aria-labelledby="api-tokens-heading">
      <h2 id="api-tokens-heading">API tokens</h2>
      <div className="ps-fields">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="pa-text">For the API, MCP clients and the AI Producer skill. A token has full access to your account.</p>
          {createDialog}
        </div>
        {loading ? (
          <ContentSkeleton label="Loading API tokens" variant="list" />
        ) : loadFailed ? (
          <Notice
            tone="error"
            title="We couldn’t load your API tokens"
            action={
              <Button variant="secondary" onClick={retryLoad}>
                <RefreshCw aria-hidden="true" />
                Try again
              </Button>
            }
          >
            Check your connection and try again.
          </Notice>
        ) : tokens.length === 0 ? (
          <EmptyState
            size="compact"
            headingLevel={3}
            icon={<KeyRound />}
            title="No API tokens yet"
            description="Create one to use with the AI Producer skill, MCP clients or the API."
          />
        ) : (
          <ul className="grid gap-3" aria-label="Your API tokens">
            {tokens.map((token) => (
              <Card
                as="li"
                key={token.id}
                headingLevel={3}
                title={token.name}
                actions={
                  <ConfirmDialog
                    trigger={
                      <IconButton label={`Revoke ${token.name}`} variant="ghost">
                        <Trash2 />
                      </IconButton>
                    }
                    title={`Revoke ${token.name}?`}
                    description="Apps, MCP clients and scripts using this token stop working right away. You can’t undo this."
                    confirmLabel="Revoke token"
                    variant="danger"
                    onConfirm={() => handleRevoke(token.id)}
                    failureMessage="The token wasn’t revoked. Check your connection and try again."
                  />
                }
              >
                <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-7">
                  <code className="pa-token-prefix">{token.token_prefix}</code>
                  <span>Created {formatDate(token.created_at)}</span>
                  <span>Last used {formatDate(token.last_used_at)}</span>
                </p>
              </Card>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}
